import React from 'react';
import { motion } from 'framer-motion';

const AdobeProjectDetails = ({ project }) => {
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
      {/* Challenges & Highlights - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Project Challenges */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-red-400">🔴 Challenges</h3>
          <div className="space-y-2">
            {project.details.challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{challenge}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Project Highlights */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-green-400">🟢 Highlights</h3>
          <div className="space-y-2">
            {project.details.highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{highlight}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Four-Panel Comic Story */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">📖 Story: From Challenge to Solution</h3>

        {/* Complete Comic Image */}
        <div className="relative rounded-lg overflow-hidden border-2 border-purple-500/30 bg-gray-900/50">
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            src="/pic/adobe/offline.webp"
            alt="Adobe Offline Signature Story - Four Panel Comic"
            className="w-full h-auto object-contain"
          />
          <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-bold">
            4-Panel Story
          </div>
        </div>

        {/* Story Description */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <h4 className="font-bold text-red-400 mb-2">Panel 1-2: The Problem</h4>
            <p className="text-gray-300">
              Insurance agents meet clients face-to-face for policy sales, especially at night.
              However, network instability or Adobe service failures prevent signature pages from loading,
              causing frustrated clients and requiring costly follow-up visits.
            </p>
          </div>

          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
            <h4 className="font-bold text-green-400 mb-2">Panel 3-4: The Solution</h4>
            <p className="text-gray-300">
              Designed an offline signature solution: clients confirm email addresses in iSpeed,
              system sends signature emails sequentially, clients sign via email links,
              and system automatically emails the next signer - a seamless automated process.
            </p>
          </div>
        </div>
      </div>

      {/* Results Metrics */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">📊 Results & Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-xl p-4 text-center"
          >
            <div className="text-4xl font-bold text-blue-400 mb-2">95%</div>
            <div className="text-sm font-bold text-gray-200 mb-1">Reduction in Failures</div>
            <p className="text-xs text-gray-400">Signature failure rate significantly decreased</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-xl p-4 text-center"
          >
            <div className="text-4xl font-bold text-green-400 mb-2">2-3</div>
            <div className="text-sm font-bold text-gray-200 mb-1">Incidents Avoided</div>
            <p className="text-xs text-gray-400">Failed signature reports prevented per month</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-xl p-4 text-center"
          >
            <div className="text-4xl font-bold text-purple-400 mb-2">24/7</div>
            <div className="text-sm font-bold text-gray-200 mb-1">Always Available</div>
            <p className="text-xs text-gray-400">Offline signing works anytime, anywhere</p>
          </motion.div>
        </div>

        {/* Overall Impact Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-4 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-cyan-500/20 border border-green-500/40 rounded-xl p-4 text-center"
        >
          <p className="text-green-400 text-base font-bold mb-1">
            Problem Solved: Monthly signature failures eliminated
          </p>
          <p className="text-xs text-gray-400">
            Clients can now sign conveniently via email, even during network issues or system maintenance windows
          </p>
        </motion.div>
      </div>

      {/* Technical Comparison */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4">⚙️ Technical Implementation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Online Signing (Original) */}
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4">
            <h4 className="font-bold text-orange-400 mb-3 flex items-center gap-2">
              <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-sm font-bold">
                Original
              </span>
              Online Signing
            </h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-orange-400">•</span>
                <span>Real-time Adobe Sign service integration</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">•</span>
                <span>Synchronous signature workflow</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">•</span>
                <span>Requires stable network connection</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">•</span>
                <span>Suitable for office environments</span>
              </li>
            </ul>
          </div>

          {/* Offline Signing (New) */}
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
              <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-bold">
                New
              </span>
              Offline Signing
            </h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-blue-400">•</span>
                <span>Asynchronous email-based signature flow</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">•</span>
                <span>Sequential multi-signer processing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">•</span>
                <span>Automatic retry mechanism with email alerts</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">•</span>
                <span>Flexible toggle between online/offline modes</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Workflow Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="flex justify-center mt-4"
        >
          <div className="bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 text-white px-6 py-2 rounded-full font-bold flex items-center gap-2 text-sm">
            <span>Online</span>
            <span className="text-lg">↔</span>
            <span>Offline</span>
            <span className="bg-white/20 px-3 py-1 rounded-full ml-2">Flexible Switching</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdobeProjectDetails;
